import './AddAppSettings.scss';

import { COPY_SPECIFIC, PLACEHOLDERS_COPY } from '@sonar-software/sonar-ts-constants';
import { FeatureFlag, StakeholderType } from '@sonar-software/sonar-core';
import { Form, FormInstance, Input, Tag } from 'antd';

import { APP_VALIDATION_RULES } from '../../@arch/validation';
import { AddAppSection } from '../add-app';
import { AppActivityPeriodSelect } from './AppActivityPeriodSelect';
import { AppUserSelect } from './AppUserSelect';
import { ClassificationSelector } from '@ui/tags';
import { FeatureBoundary } from '@ui/features';
import React from 'react';
import { StakeholderSelector } from '@ui/StakeholderSelector';
import TextArea from 'antd/es/input/TextArea';
import { UserSelector } from '@ui/UserSelector';
import { useFeatureFlag } from '@hooks';

export interface AddAppSettingsProps {
	applicationName: string;
	form: FormInstance<AddAppForm>;
	onFinish?: () => void;
	onValuesChange?: (changedValues: Partial<AddAppForm>) => void;
	validator?: () => void
}

export interface AddAppForm {
	description?: string;
	classification?: string;
	group?: number;
	label?: string;
	name: string;
	owner?: number;
	stakeholderIds?: Array<number>;
	user: string;
	activityPeriod: number;
}

export const AddAppSettings: React.FC<AddAppSettingsProps> = ({
	applicationName,
	form,
	onFinish,
	onValuesChange,
	validator
}) => {
	const hasEventMonitoring = useFeatureFlag(FeatureFlag.DataSecurityDashboard);

	return (
		<AddAppSection
			className='add-app-settings-section'
			title={ COPY_SPECIFIC.ADD_APP_SETTINGS }>
			<Form
				className='add-app-settings-form'
				form={ form }
				layout='vertical'
				onFinish={ onFinish }
				onValuesChange={ onValuesChange }>
				<Form.Item
					className='app-display'
					id='app-name'
					label={ COPY_SPECIFIC.NAME }
					name='name'
					rules={ [
						...APP_VALIDATION_RULES.NAME,
						{
							validator: validator
						}
					] }>
					<Tag className='application-name'>{ applicationName }</Tag>
				</Form.Item>
				<Form.Item
					id='app-user'
					label={ COPY_SPECIFIC.INTEGRATION_USER }
					name='user'>
					<AppUserSelect
						allowClear
						disabled={ !hasEventMonitoring }
						disableUsed />
				</Form.Item>
				<Form.Item
					id='app-label'
					label={ COPY_SPECIFIC.LABEL }
					name='label'
					rules={ [
						...APP_VALIDATION_RULES.LABEL,
						{
							validator: validator
						}
					] }>
					<Input placeholder={ PLACEHOLDERS_COPY.APP_LABEL } />
				</Form.Item>
				<Form.Item
					id='app-description'
					label={ COPY_SPECIFIC.DESCRIPTION }
					name='description'
					rules={ APP_VALIDATION_RULES.DESCRIPTION }>
					<TextArea placeholder={ PLACEHOLDERS_COPY.APP_DESCRIPTION } />
				</Form.Item>
				<Form.Item
					id='app-owner'
					label={ COPY_SPECIFIC.OWNER }
					name='owner'>
					<UserSelector
						allowClear
						avatar
						placeholder={ PLACEHOLDERS_COPY.ADD_OWNER } />
				</Form.Item>
				<Form.Item
					id='app-stakeholder'
					label={ COPY_SPECIFIC.STAKEHOLDERS }
					name='stakeholderIds'>
					<StakeholderSelector
						direction='horizontal'
						mode='multiple'
						placeholder={ COPY_SPECIFIC.ADD_STAKEHOLDERS } />
				</Form.Item>
				<Form.Item
					id='app-group'
					label={ COPY_SPECIFIC.GROUP }
					name='group'>
					<StakeholderSelector
						placeholder={ PLACEHOLDERS_COPY.SELECT_APP_GROUP }
						stakeholderType={ StakeholderType.Team } />
				</Form.Item>
				<Form.Item
					id='app-classification'
					label={ COPY_SPECIFIC.CLASSIFICATION }
					name='classification'>
					<ClassificationSelector isSpreadsheet />
				</Form.Item>
				<FeatureBoundary feature={ FeatureFlag.DataSecurityDashboard }>
					<Form.Item
						id='app-activity-period'
						initialValue={ 7 }
						label={ COPY_SPECIFIC.OBJECT_AND_FIELD_USAGE_ACTIVITY }
						name='activityPeriod'>
						<AppActivityPeriodSelect />
					</Form.Item>
				</FeatureBoundary>
			</Form>
		</AddAppSection>);
};
